import { Tab, Row, Col, Table, Button, Nav, Placeholder } from 'react-bootstrap'
import React, { useState } from 'react'
import { useParams, useNavigate, useLocation, Link } from 'react-router'
import MediaSidebar from '@/components/MediaSidebar'
import './PortalDetails.scss'
import { marked } from 'marked'
import PortalProjectsInterviews from '@/components/PortalProjectsInterviews'
import AttachmentsList from '@/components/shared/AttachmentsList'
import QuestionsAnswersControl from '@/components/QuestionsAnswersControl'
import { userCurrentUser } from '@/features/apiSliceWithCurrentUser'
import Project from '@/types/Project'
import Interview from '@/types/Interview'
import PortalAdminControls from '@/components/PortalAdminControls'
import useEmbedded from '@/hooks/useEmbedded'
import { TitledRecordsSelectOptionValueType } from '@/components/TitledRecordsSelect'
import { trackEvent } from '@/utils/analytics'
import { useGetPortalQuery } from '@/features/apiSliceWithPortals'
import classNames from 'classnames'
import useToastQuery from '@/hooks/useToastQuery'


const VALID_TABS = ['questions', 'description', 'content'] as const
const TAB_TITLES: Record<PortalTab, string> = {
  description: 'Portal\'s description',
  questions: 'Questions',
  content: 'Available content',
}

type PortalTab = typeof VALID_TABS[number]

function isPortalTab(value: string | null | undefined): value is PortalTab {
  return Boolean(value && VALID_TABS.includes(value as PortalTab))
}

export default function PortalDetails() {
  const currentUser = userCurrentUser()
  const { portalSlug, tab } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: portal, isFetching } = useToastQuery(useGetPortalQuery, portalSlug!)
  const activeTab: PortalTab = isPortalTab(tab) ? tab : 'questions'
  const [selectedProject, setSelectedProject] = useState<Required<Project>>()
  const [selectedInterview, setSelectedInterview] = useState<TitledRecordsSelectOptionValueType<Interview>>()
  const isEmbedded = useEmbedded()

  const buildPortalTabPath = (nextTab: PortalTab) => {
    const segments = pathname.split('/').filter(Boolean)

    if (segments[0] === 'portals') {
      return `/portals/${portalSlug}/${nextTab}`
    }

    return `/${portalSlug}/${nextTab}`
  }

  const openProjectInterview = (
    project: Required<Project>,
    interview: TitledRecordsSelectOptionValueType<Interview> = project.interviews!,
  ) => {
    setSelectedProject(project)
    setSelectedInterview(interview)
    navigate(buildPortalTabPath('content'))
  }

  if (!portal || isFetching) return <PortalDetailsSkeleton />

  return (
    <Row className={classNames('PortalDetails justify-content-center g-4')}>
      <Col xs={12} lg={!portal?.photos && isEmbedded ? 12 : 8} className="order-2 order-lg-1">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <h3 className="my-4">{portal.title}</h3>
          {isEmbedded && (
            <a
              href={`${window.location.origin}/${portal.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-secondary btn-sm ms-auto"
            >
              <i className="fas fa-external-link-alt me-1" />
              Open full page
            </a>
          )}
          {currentUser?.role === 'admin' && <PortalAdminControls className="ms-auto" portal={portal} />}
        </div>
        <Tab.Container
          activeKey={activeTab}
          onSelect={(tabKey) => {
            if (!isPortalTab(tabKey)) return

            trackEvent('portal_tab_change', { tab: tabKey })
          }}
          unmountOnExit
        >
          <Nav variant="tabs" className="mb-3" id="portalTabs">
            {VALID_TABS.map((tabKey) => (
              <Nav.Item key={tabKey}>
                <Nav.Link
                  as={Link}
                  to={buildPortalTabPath(tabKey)}
                  eventKey={tabKey}
                >
                  {TAB_TITLES[tabKey]}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <Tab.Content>
            <Tab.Pane
              className="PortalDetails__Description d-flex flex-column gap-3"
              eventKey="description"
            >
              <div
                dangerouslySetInnerHTML={{ __html: marked(portal.description) }}
                className="d-flex flex-column gap-3 mx-2 mt-4"
              />
              <Table responsive="lg">
                <thead>
                <tr>
                  <th>Contribution:</th>
                  <th>Content</th>
                  <th>Downloads</th>
                </tr>
                </thead>
                <tbody>
                {(portal.projects as Required<Project>[])?.map((project) => (
                    <tr key={project.id}>
                      <td>
                        <Button
                          variant="link"
                          className="text-start"
                          onClick={() => openProjectInterview(project)}
                        >
                          {project.title}
                        </Button>
                      </td>
                      <td>
                        <ul>
                          {project.interviews?.map(i => (
                            <li key={i.id}>
                              <Button
                                variant="link"
                                className="text-start"
                                onClick={() => openProjectInterview(project, i)}
                              >
                                {i.title}
                              </Button>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td>
                        {project.attachments && <AttachmentsList attachments={project.attachments} />}
                      </td>
                    </tr>
                  ),
                )}
                </tbody>
              </Table>
            </Tab.Pane>
            <Tab.Pane eventKey="questions">
              <QuestionsAnswersControl portal={portal} />
            </Tab.Pane>
            <Tab.Pane
              className="PortalDetails__PortalProjectsInterviews d-flex flex-column gap-3"
              eventKey="content"
            >
              <PortalProjectsInterviews
                projects={portal.projects as Required<Project>[]}
                project={selectedProject}
                interviews={selectedInterview}
              />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Col>
      {/* Right sidebar (audio + photos) */}
      {portal?.photos && (
        <Col xs={12} lg={3} className="PortalDetails__SidebarColumn order-1 order-lg-2">
          <MediaSidebar
            imageUrls={portal?.photos?.map(photo => photo.url) ?? []}
            className="w-100"
          />
        </Col>
      )}
    </Row>
  )
}

function PortalDetailsSkeleton() {
  return (
    <Row className="PortalDetails justify-content-center g-4">
      <Col xs={12} lg={8} className="order-2 order-lg-1">
        {/* Title and admin controls */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <Placeholder as="h3" animation="wave" className="my-4">
            <Placeholder xs={6} />
          </Placeholder>
          <div className="d-flex gap-2">
            <Placeholder animation="wave">
              <Placeholder xs={3} style={{ height: '38px' }} />
            </Placeholder>
          </div>
        </div>

        {/* Tabs navigation */}
        <div className="mb-3 pb-2 border-bottom">
          <Placeholder as="div" animation="wave" className="d-flex gap-3">
            <Placeholder xs={2} />
            <Placeholder xs={2} />
            <Placeholder xs={2} />
          </Placeholder>
        </div>

        {/* Tab content - table rows */}
        <div className="d-flex flex-column gap-3">
          {[...Array(5)].map((_, i) => (
            <Placeholder as="div" animation="wave" key={i} className="p-3 border rounded">
              <Placeholder xs={8} className="mb-2" />
              <Placeholder xs={6} />
            </Placeholder>
          ))}
        </div>
      </Col>

      {/* Right sidebar (media) */}
      <Col xs={12} lg={3} className="PortalDetails__SidebarColumn order-1 order-lg-2">
        <Placeholder as="div" animation="wave" className="rounded" style={{ height: '300px' }} />
      </Col>
    </Row>
  )
}
